import { ReactElement, useMemo } from 'react';

import { DATE_FE_FORMAT } from '@/constants';
import { Dayts } from '@/utils';
import { TrainingReportPdfData, formatZero } from '@/features/PdfLayout/layouts/TrainingReport/utils';
import { countUniqueDrivers } from '@/features/PdfLayout/layouts/TrainingReport/TrainingReportPdfTable/utils';
import { usePdfLayout } from '@/features/PdfLayout';

const TOTAL_EMPLOYEES_KEY = /total\s*employees/i;
// Splits "12 Days" into a bold value and a smaller unit, matching the design
const VALUE_WITH_UNIT = /^(\S+)\s+(.+)$/;

const formatDate = (value?: string): string => (value ? new Dayts(value).format(DATE_FE_FORMAT) : '');

const TrainingReportPdfSummary = (): ReactElement => {
  const { params, header, cities } = usePdfLayout(({ state }) => state.data as TrainingReportPdfData);

  const [kpis, totalEmployees] = useMemo(() => {
    // Always count distinct drivers ourselves: a driver trained several times appears in several rows,
    // so any BE row count would overstate it. A BE "Total Employees" header entry is dropped from the KPIs.
    const entries = Object.entries(header || {}).filter(([key]) => !TOTAL_EMPLOYEES_KEY.test(key));
    return [entries, countUniqueDrivers(cities)];
  }, [header, cities]);

  const airports = (params.input.airports as string[] | null)?.join(', ') || 'All';

  return (
    <div className="TrainingReportPdf-Summary print-break-avoid">
      <div className="TrainingReportPdf-Filters">
        <span>
          Dates: {formatDate(params.input.startDatetime)} - {formatDate(params.input.endDatetime)}
        </span>
        <span>Airports: {airports}</span>
        <span>Total Employees: {totalEmployees}</span>
      </div>
      <div className="TrainingReportPdf-Kpis">
        {kpis.map(([key, { value }]) => {
          const [, amount, unit] = String(value ?? '').match(VALUE_WITH_UNIT) || [null, value ?? '--', null];
          return (
            <div className="TrainingReportPdf-Kpi" data-name={key} key={key}>
              <span className="TrainingReportPdf-KpiLabel">{key}</span>
              <span className="TrainingReportPdf-KpiValue">
                {formatZero(String(amount))}
                {unit && <small> {unit}</small>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrainingReportPdfSummary;
